import React from 'react';
import { Link } from 'react-router-dom';
import SEO from '@/components/SEO';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import GenAIStrategySection from '@/components/solutions/GenAIStrategySection';

const IntegratedRetailMasterDataModel = () => {
  return (
    <div className="flex flex-col min-h-screen">
      <SEO
        title="Integrated Retail Master Data Model | Stratodata"
        description="A unified data model built for retail — connecting products, orders, customers and financials into one consistent source of truth."
        canonical="/solutions/integrated-retail-master-data-model"
      />
      <Navigation />
      <main className="index-page px-7 md:px-10 flex-1">

        {/* Hero */}
        <section className="relative pt-24 pb-8 md:pt-28 md:pb-12 overflow-visible bg-white">
          <div
            className="relative mx-auto flex flex-col items-center justify-start text-center bg-no-repeat bg-center bg-contain min-h-[460px] md:min-h-[620px] px-6 pt-[110px] md:pt-[142px] pb-6"
            style={{
              backgroundImage: 'url(/lovable-uploads/cloud.png)',
              maxWidth: 'min(96vw, 1320px)',
              width: '100%',
            }}
          >
            <span className="inline-block px-4 py-2 rounded-lg text-white text-sm font-medium mb-5" style={{ backgroundColor: '#2A1E48' }}>
              Retail Management
            </span>
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-foreground mb-4">
              Integrated Retail Master Data Model
            </h1>
            <p className="text-muted-foreground text-base md:text-lg leading-relaxed mb-2">
              One verified dataset. Every retail source connected.
            </p>
            <p className="text-muted-foreground text-base md:text-lg leading-relaxed mb-2">
              Built for accuracy. Designed for speed.
            </p>
            <p className="text-muted-foreground text-base md:text-lg leading-relaxed mb-6">
              The data foundation every retail decision should rest on.
            </p>
            <Link
              to="/tell-me-more"
              className="inline-flex items-center justify-center px-8 py-3 rounded-lg bg-[#FF6B35] text-white font-medium hover:bg-[#FF6B35]/90 transition-colors"
            >
              Contact us
            </Link>
          </div>
          <div className="relative z-10 flex justify-center -mt-[60px] md:-mt-[200px] px-0">
            <img
              src="/lovable-uploads/Data_warehousing.png"
              alt="Integrated Retail Master Data Model"
              className="w-full max-w-3xl md:max-w-4xl h-auto object-contain drop-shadow-lg"
            />
          </div>
        </section>

        {/* Challenge */}
        <section id="challenge" className="pt-8 md:pt-12 pb-16 md:pb-24 bg-white">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-8">
              <span
                className="inline-block px-5 py-2.5 rounded-lg text-white text-sm font-medium"
                style={{ backgroundColor: '#2A1E48' }}
              >
                Challenge
              </span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-4">
              Why Retail Data Is So Often{' '}
              <span className="text-[#FF6B35]">Broken</span>
            </h2>
            <p className="text-foreground/90 text-center text-lg max-w-3xl mx-auto mb-12">
              Most retailers operate on fragmented, unverified data — and make million-euro decisions on top of it.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {[
                {
                  title: 'Data Silos Everywhere',
                  text: 'ERP lives in one system, marketing data in another, web analytics in a third. Each team works from their own extract, their own version of the truth — leading to conflicting numbers and wasted hours reconciling reports instead of acting on them.',
                },
                {
                  title: 'No Single Source of Truth',
                  text: 'When the finance team\'s revenue number doesn\'t match the ecommerce team\'s, trust in data collapses. Without a single verified master dataset, even the most capable analysts spend most of their time firefighting data quality instead of generating insight.',
                },
                {
                  title: 'Garbage In, Garbage Out',
                  text: 'Most automation projects fail not because of bad logic — but because of bad input data. Pricing algorithms, bidding tools and forecasters built on inconsistent source data will consistently produce wrong outputs, no matter how sophisticated the model.',
                },
                {
                  title: 'Every New Tool Starts from Scratch',
                  text: 'Each time a new analytics or automation tool is added, the same data connections need to be rebuilt — with slightly different logic, slightly different definitions. Without a master model, the data stack becomes increasingly brittle and expensive to maintain.',
                },
              ].map((item) => (
                <div
                  key={item.title}
                  className="rounded-xl p-6 text-left shadow-sm border border-orange-50/60 min-h-full"
                  style={{ background: 'linear-gradient(to bottom, #FFECD3 0%, #ffffff 100%)' }}
                >
                  <h3 className="font-bold text-foreground text-lg mb-3">{item.title}</h3>
                  <p className="text-foreground/90 text-sm leading-relaxed">{item.text}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Solution */}
        <section className="pt-0 pb-16 md:pb-24 bg-white">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-6">
              <span
                className="inline-block px-5 py-2.5 rounded-lg text-white text-sm font-medium"
                style={{ backgroundColor: '#2A1E48' }}
              >
                Solution
              </span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-4">
              One Model. All Your Retail Data.
            </h2>
            <p className="text-muted-foreground text-center max-w-2xl mx-auto mb-16">
              We design and build a standardised, verified omni-channel retail data model — so every team, tool and automation works from the same foundation.
            </p>

            {/* Block 1 */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-16 items-center mb-20">
              <div>
                <h3 className="text-2xl font-bold text-foreground mb-4">All Sources, One Schema</h3>
                <p className="text-muted-foreground mb-4">
                  We connect your ERP, ecommerce platform, marketing channels, WMS and web analytics into a single standardised schema — designed from the ground up for retail.
                </p>
                <p className="text-muted-foreground mb-4">
                  Drawing on dozens of successful implementations, we know exactly which fields to pull, how to normalise them and where the edge cases hide.
                </p>
                <p className="text-muted-foreground">
                  The result: your marketing, sales, stock and web data available in one place, refreshed daily, with consistent definitions across every report.
                </p>
              </div>
              <div className="rounded-[24px] pt-5 pl-5 flex justify-end items-end w-full max-w-[460px] min-h-[275px]" style={{ backgroundColor: '#FFF7EC' }}>
                <div className="rounded-[20px] p-5 grid grid-cols-3 gap-6 w-full max-w-[400px] md:min-w-[400px] md:w-[400px] place-content-center place-items-center" style={{ backgroundColor: '#E0E7FF' }}>
                  {[
                    { file: 'ga4_icon.png', alt: 'GA4' },
                    { file: 'sap_icon.png', alt: 'SAP' },
                    { file: 'sheets_icon.png', alt: 'Google Sheets' },
                    { file: 'shopify_icon.png', alt: 'Shopify' },
                    { file: 'ads_icon.png', alt: 'Google Ads' },
                    { file: 'abra_icon.png', alt: 'Abra' },
                    { file: 'magento_icon.png', alt: 'Magento' },
                    { file: 'shoptet_icon.png', alt: 'Shoptet' },
                    { file: 'excel_icon.png', alt: 'Excel', small: true },
                  ].map((logo) => (
                    <div key={logo.alt} className="rounded-full w-12 h-12 flex items-center justify-center bg-white shadow-sm flex-shrink-0 p-1.5">
                      <img
                        src={`/lovable-uploads/${logo.file}`}
                        alt={logo.alt}
                        className={`w-full h-full object-contain ${logo.small ? 'max-w-[70%] max-h-[70%]' : ''}`}
                      />
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Block 2 */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-16 items-center mb-20">
              <div className="order-2 md:order-1 rounded-[24px] pt-5 pr-5 pl-0 pb-0 flex justify-start items-end w-full max-w-[460px] min-h-[275px]" style={{ backgroundColor: '#FFF7EC' }}>
                <div className="flex-1 min-w-0 flex justify-start items-end">
                  <img
                    src="/lovable-uploads/reporting_and_bi.png"
                    alt="Data quality and verification layer"
                    className="w-full h-auto object-contain object-bottom rounded-2xl max-h-[245px]"
                  />
                </div>
              </div>
              <div className="order-1 md:order-2">
                <h3 className="text-2xl font-bold text-foreground mb-4">Built-In Data Quality</h3>
                <p className="text-muted-foreground mb-4">
                  A model is only as good as the data flowing through it. Every layer of our model includes validation logic — flagging anomalies, missing values and reconciliation errors before they reach your dashboards.
                </p>
                <p className="text-muted-foreground mb-4">
                  We define shared business logic centrally: what counts as a sale, how returns are handled, how marketing cost is attributed — so every downstream tool agrees.
                </p>
                <p className="text-muted-foreground">
                  Your team stops arguing about numbers and starts acting on them.
                </p>
              </div>
            </div>

            {/* Block 3 */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-16 items-center">
              <div>
                <h3 className="text-2xl font-bold text-foreground mb-4">The Foundation for Everything Else</h3>
                <ul className="space-y-3 text-muted-foreground">
                  {[
                    'Plug-and-play ready for all RAS automation modules — no re-integration needed.',
                    'Consistent KPI definitions shared across finance, marketing and operations.',
                    'Daily data freshness with automated pipeline monitoring and alerting.',
                    'Scalable schema that grows with your catalogue, channels and markets.',
                    'Full documentation so your team understands and owns the model.',
                  ].map((item, i) => (
                    <li key={i} className="flex gap-3">
                      <span className="flex-shrink-0 w-2 h-2 rounded-full mt-2 bg-[#FFECD3]" style={{ minWidth: 8, minHeight: 8 }} aria-hidden />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="rounded-[24px] pt-5 pl-5 pr-0 pb-0 flex justify-end items-end w-full max-w-[460px] min-h-[275px]" style={{ backgroundColor: '#FFF7EC' }}>
                <div className="flex-1 min-w-0 flex justify-end items-end">
                  <img
                    src="/lovable-uploads/data_warehous.png"
                    alt="Retail data warehouse architecture"
                    className="w-auto max-w-full h-auto object-contain object-bottom object-right rounded-2xl max-h-[245px]"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Banner */}
        <section className="relative overflow-hidden py-20">
          <div className="absolute inset-0 bg-gradient-to-br from-[#FF6B35] via-[#FF7F27] to-[#FF4500] rounded-3xl" />
          <div className="container-custom relative z-10">
            <div className="max-w-3xl mx-auto text-center pt-16 pb-8 px-4">
              <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-4 text-white leading-tight">
                Build on Solid Ground
              </h2>
              <p className="text-lg md:text-xl text-white mb-10 max-w-xl mx-auto">
                Every automation, every AI feature, every strategic decision your business makes — starts with the right data model. Let's build yours.
              </p>
              <div className="flex justify-center">
                <Link
                  to="/tell-me-more"
                  className="inline-flex items-center justify-center px-8 py-4 rounded-lg bg-[#2a1e48] hover:bg-[#2a1e48]/90 text-white text-base font-medium transition-all duration-300 shadow-lg border border-white/10"
                >
                  Contact us
                </Link>
              </div>
            </div>
          </div>
        </section>

        <GenAIStrategySection />
      </main>
      <Footer />
    </div>
  );
};

export default IntegratedRetailMasterDataModel;
